/**
 * Connector path calculation utilities
 */

import { AnchorPoint, ConnectorPathType } from '../editing/types';

/**
 * Calculate straight line path points
 */
export function calculateStraightPath(
  startAnchor: AnchorPoint,
  endAnchor: AnchorPoint
): number[] {
  return [startAnchor.x, startAnchor.y, endAnchor.x, endAnchor.y];
}

/**
 * Calculate curved (bezier) path
 */
export function calculateCurvedPath(
  startAnchor: AnchorPoint,
  endAnchor: AnchorPoint,
  curvature: number = 0.5
): { points: number[]; controlPoints: Array<{ x: number; y: number }> } {
  const dx = endAnchor.x - startAnchor.x;
  const dy = endAnchor.y - startAnchor.y;
  
  // Control points extend horizontally first for smooth curves
  const controlDist = Math.abs(dx) * curvature;
  const cp1x = startAnchor.x + (dx > 0 ? controlDist : -controlDist);
  const cp1y = startAnchor.y;
  const cp2x = endAnchor.x - (dx > 0 ? controlDist : -controlDist);
  const cp2y = endAnchor.y;
  
  return {
    points: [startAnchor.x, startAnchor.y, cp1x, cp1y, cp2x, cp2y, endAnchor.x, endAnchor.y],
    controlPoints: [
      { x: cp1x, y: cp1y },
      { x: cp2x, y: cp2y },
    ],
  };
}

/**
 * Calculate bent (elbow) path with 90-degree angles
 */
export function calculateBentPath(
  startAnchor: AnchorPoint,
  endAnchor: AnchorPoint,
  controlPoints?: Array<{ x: number; y: number }>
): number[] {
  if (controlPoints && controlPoints.length > 0) {
    // Use provided control points for custom bent path
    const points: number[] = [startAnchor.x, startAnchor.y];
    for (const cp of controlPoints) {
      points.push(cp.x, cp.y);
    }
    points.push(endAnchor.x, endAnchor.y);
    return points;
  }
  
  // Default: simple elbow (horizontal then vertical or vice versa)
  const dx = endAnchor.x - startAnchor.x;
  const dy = endAnchor.y - startAnchor.y;
  
  // Determine if we should go horizontal first or vertical first
  const absDx = Math.abs(dx);
  const absDy = Math.abs(dy);
  
  if (absDx > absDy) {
    // Horizontal first
    const midX = (startAnchor.x + endAnchor.x) / 2;
    return [
      startAnchor.x,
      startAnchor.y,
      midX,
      startAnchor.y,
      midX,
      endAnchor.y,
      endAnchor.x,
      endAnchor.y,
    ];
  } else {
    // Vertical first
    const midY = (startAnchor.y + endAnchor.y) / 2;
    return [
      startAnchor.x,
      startAnchor.y,
      startAnchor.x,
      midY,
      endAnchor.x,
      midY,
      endAnchor.x,
      endAnchor.y,
    ];
  }
}

/**
 * Find the closest point on a path to a given point
 * Returns the position (0-1) along the path and the point coordinates
 */
export function findClosestPointOnPath(
  path: number[],
  targetX: number,
  targetY: number
): { position: number; x: number; y: number; angle: number } {
  if (path.length < 4) {
    return { position: 0, x: path[0] || 0, y: path[1] || 0, angle: 0 };
  }

  let minDistance = Infinity;
  let bestPosition = 0;
  let bestPoint = { x: path[0], y: path[1] };
  let bestAngle = 0;

  // For straight lines, calculate directly
  if (path.length === 4) {
    const x1 = path[0];
    const y1 = path[1];
    const x2 = path[2];
    const y2 = path[3];
    
    // Project point onto line segment
    const dx = x2 - x1;
    const dy = y2 - y1;
    const lengthSq = dx * dx + dy * dy;
    
    if (lengthSq === 0) {
      return { position: 0, x: x1, y: y1, angle: 0 };
    }
    
    const t = Math.max(0, Math.min(1, ((targetX - x1) * dx + (targetY - y1) * dy) / lengthSq));
    const x = x1 + t * dx;
    const y = y1 + t * dy;
    const distance = Math.sqrt((targetX - x) ** 2 + (targetY - y) ** 2);
    
    if (distance < minDistance) {
      minDistance = distance;
      bestPosition = t;
      bestPoint = { x, y };
      bestAngle = Math.atan2(dy, dx);
    }
  } else if (path.length === 8) {
    // For bezier curves, sample multiple points
    const samples = 100;
    for (let i = 0; i <= samples; i++) {
      const t = i / samples;
      const point = getPointAlongPath(path, t);
      const distance = Math.sqrt((targetX - point.x) ** 2 + (targetY - point.y) ** 2);
      
      if (distance < minDistance) {
        minDistance = distance;
        bestPosition = t;
        bestPoint = { x: point.x, y: point.y };
        bestAngle = point.angle;
      }
    }
  } else {
    // For multi-segment paths (bent), check each segment
    let totalLength = 0;
    const segmentLengths: number[] = [];
    
    // Calculate segment lengths
    for (let i = 0; i < path.length - 2; i += 2) {
      const x1 = path[i];
      const y1 = path[i + 1];
      const x2 = path[i + 2];
      const y2 = path[i + 3];
      const length = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
      segmentLengths.push(length);
      totalLength += length;
    }
    
    // Find closest point on each segment
    let accumulatedLength = 0;
    for (let seg = 0; seg < segmentLengths.length; seg++) {
      const x1 = path[seg * 2];
      const y1 = path[seg * 2 + 1];
      const x2 = path[seg * 2 + 2];
      const y2 = path[seg * 2 + 3];
      
      const dx = x2 - x1;
      const dy = y2 - y1;
      const lengthSq = dx * dx + dy * dy;
      
      if (lengthSq > 0) {
        const t = Math.max(0, Math.min(1, ((targetX - x1) * dx + (targetY - y1) * dy) / lengthSq));
        const x = x1 + t * dx;
        const y = y1 + t * dy;
        const distance = Math.sqrt((targetX - x) ** 2 + (targetY - y) ** 2);
        
        if (distance < minDistance) {
          minDistance = distance;
          const segmentPosition = (accumulatedLength + t * segmentLengths[seg]) / totalLength;
          bestPosition = segmentPosition;
          bestPoint = { x, y };
          bestAngle = Math.atan2(dy, dx);
        }
      }
      
      accumulatedLength += segmentLengths[seg];
    }
  }

  return {
    position: bestPosition,
    x: bestPoint.x,
    y: bestPoint.y,
    angle: bestAngle,
  };
}

/**
 * Calculate point along a path for text positioning
 * Returns position, angle for text rotation (in radians)
 */
export function getPointAlongPath(
  path: number[],
  position: number  // 0-1
): { x: number; y: number; angle: number } {
  if (path.length < 4) {
    // Fallback for invalid paths
    return { x: path[0] || 0, y: path[1] || 0, angle: 0 };
  }
  
  // For straight lines (2 points)
  if (path.length === 4) {
    const x1 = path[0];
    const y1 = path[1];
    const x2 = path[2];
    const y2 = path[3];
    
    const x = x1 + (x2 - x1) * position;
    const y = y1 + (y2 - y1) * position;
    const angle = Math.atan2(y2 - y1, x2 - x1);
    
    return { x, y, angle };
  }
  
  // For bezier curves (4 points: start, cp1, cp2, end)
  if (path.length === 8) {
    // Approximate position along bezier curve
    const t = position;
    const t2 = t * t;
    const t3 = t2 * t;
    const mt = 1 - t;
    const mt2 = mt * mt;
    const mt3 = mt2 * mt;
    
    const x1 = path[0];
    const y1 = path[1];
    const cp1x = path[2];
    const cp1y = path[3];
    const cp2x = path[4];
    const cp2y = path[5];
    const x2 = path[6];
    const y2 = path[7];
    
    const x = mt3 * x1 + 3 * mt2 * t * cp1x + 3 * mt * t2 * cp2x + t3 * x2;
    const y = mt3 * y1 + 3 * mt2 * t * cp1y + 3 * mt * t2 * cp2y + t3 * y2;
    
    // Calculate tangent angle
    const dx = 3 * mt2 * (cp1x - x1) + 6 * mt * t * (cp2x - cp1x) + 3 * t2 * (x2 - cp2x);
    const dy = 3 * mt2 * (cp1y - y1) + 6 * mt * t * (cp2y - cp1y) + 3 * t2 * (y2 - cp2y);
    const angle = Math.atan2(dy, dx);
    
    return { x, y, angle };
  }
  
  // For multi-segment paths (bent connectors)
  // Find which segment we're on
  const segmentCount = (path.length - 2) / 2;
  const segmentIndex = Math.floor(position * segmentCount);
  const segmentPosition = (position * segmentCount) % 1;
  
  const startIndex = segmentIndex * 2;
  const endIndex = Math.min(startIndex + 2, path.length - 2);
  
  const x1 = path[startIndex];
  const y1 = path[startIndex + 1];
  const x2 = path[endIndex];
  const y2 = path[endIndex + 1];
  
  const x = x1 + (x2 - x1) * segmentPosition;
  const y = y1 + (y2 - y1) * segmentPosition;
  const angle = Math.atan2(y2 - y1, x2 - x1);
  
  return { x, y, angle };
}

/**
 * Calculate path points based on connector type
 */
export function calculatePathPoints(
  pathType: ConnectorPathType,
  startAnchor: AnchorPoint,
  endAnchor: AnchorPoint,
  controlPoints?: Array<{ x: number; y: number }>
): number[] {
  switch (pathType) {
    case 'straight':
      return calculateStraightPath(startAnchor, endAnchor);
    case 'curved':
      // Use custom control points if provided, otherwise calculate default
      if (controlPoints && controlPoints.length >= 2) {
        return [
          startAnchor.x,
          startAnchor.y,
          controlPoints[0].x,
          controlPoints[0].y,
          controlPoints[1].x,
          controlPoints[1].y,
          endAnchor.x,
          endAnchor.y,
        ];
      }
      return calculateCurvedPath(startAnchor, endAnchor).points;
    case 'bent':
      return calculateBentPath(startAnchor, endAnchor, controlPoints);
    default:
      return calculateStraightPath(startAnchor, endAnchor);
  }
}

